'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNovu } from '@novu/react';
import { useAuth } from '@/infrastructure/auth';
import { notificationStrategyRegistry } from './registry';
import type { NotificationRealtimeContext, UserRealtimeContext } from './types';

interface NotificationEvent {
  result: {
    data?: Record<string, unknown>;
    tags?: string[];
  };
}

/**
 * Extracts the module name from a notification.
 * Prefers explicit `realtimeContext.module`, falls back to matching
 * workflow-level tags against registered strategy modules.
 */
function resolveModule(event: NotificationEvent): {
  module: string;
  rtCtx: NotificationRealtimeContext | null;
} | null {
  const data = event.result.data;
  const rtCtx = data?.realtimeContext as NotificationRealtimeContext | undefined;

  if (rtCtx?.module) {
    return { module: rtCtx.module, rtCtx };
  }

  // Fallback: infer module from workflow tags (e.g., ["i18n", "workflow", ...])
  const tags = event.result.tags;
  if (tags) {
    for (const tag of tags) {
      if (notificationStrategyRegistry.get(tag)) {
        return { module: tag, rtCtx: null };
      }
    }
  }

  return null;
}

/**
 * Listens for Novu WebSocket `notification_received` events and
 * dispatches to the registered module strategy for React Query invalidation.
 *
 * Must be mounted inside both `NovuProvider` and `QueryClientProvider`.
 */
export function useNotificationRealtimeProcessor(): void {
  const novu = useNovu();
  const queryClient = useQueryClient();
  const { user, tenantUuid } = useAuth();

  useEffect(() => {
    if (!user) return;

    const userCtx: UserRealtimeContext = {
      userUuid: user.uuid,
      tenantUuid: tenantUuid ?? null,
    };

    const cleanup = novu.on('notifications.notification_received', (event: NotificationEvent) => {
      const resolved = resolveModule(event);
      if (!resolved) return;

      const { module, rtCtx } = resolved;
      const strategy = notificationStrategyRegistry.get(module);
      if (!strategy) return;

      // When realtimeContext is available, apply filtering (skip own action, tenant mismatch).
      // When falling back to tags, always invalidate (no context to filter on).
      if (rtCtx && !strategy.shouldProcess(rtCtx, userCtx)) return;

      strategy.invalidate(rtCtx ?? { module, action: 'unknown', affectedUuids: [], triggeredByUserId: null, tenantId: null }, queryClient);
    });

    return cleanup;
  }, [novu, queryClient, user, tenantUuid]);
}
